import { createContext, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { registerSWSilent, subscribePush, currentPermission } from "@/lib/push";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data.user);
      } catch (err) {
        if (err?.response?.data?.detail?.code === "ACCOUNT_SUSPENDED") toast.error(err.response.data.detail.message, { duration: 10000 });
        setUser(null);
      } finally {
        setLoading(false);
      }
    })();
    registerSWSilent();
  }, []);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const perm = await currentPermission();
        if (perm === "granted") {
          // Already granted — silently refresh subscription
          await subscribePush();
        }
      } catch { /* push optional */ }
    })();
  }, [user]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.requires_2fa || data.requires_2fa_setup) return data;
    setUser(data.user);
    return data.user;
  };
  const completeTwoFactor = (data) => setUser(data.user);

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* already logged out */ }
    setUser(null);
    try { const ks = await caches.keys(); await Promise.all(ks.filter((k) => k.startsWith("tl-data")).map((k) => caches.delete(k))); } catch { /* no SW */ }
  };

  return (
    <AuthCtx.Provider value={useMemo(() => ({ user, loading, login, register, logout, setUser, completeTwoFactor }), [user, loading])}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() { return useContext(AuthCtx); }
